import { useState, useMemo, useEffect } from "react";
import { uploadDocumentAction } from "../../actions/uploadDocumentAction";
import { fetchRequiredDocumentsAction } from "../../actions/fetchRequiredDocumentsAction";
import { toast } from "react-toastify";

export const useUploadDocuments = (searchParams) => {
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [errors, setErrors] = useState({});
  const [uploadingStatus, setUploadingStatus] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});
  const [requiredDocumentsField, setRequiredDocuments] = useState([]);
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);

  const entityCategory = searchParams.get("entityCategory");
  const selectedlobtypenationality = searchParams.get("selectedlobtypenationality");
  const entityName = searchParams.get("entityName");
  const userId = searchParams.get("onboardingId");
  const entityId = searchParams.get("entityId");
  console.log("USer id ..... : ", userId);

  // Fetch required documents based on userId
  useEffect(() => {
    const fetchRequiredDocuments = async () => {
      if (!userId) {
        return;
      }

      setIsLoadingDocuments(true);

      try {
        const result = await fetchRequiredDocumentsAction(userId);
        console.log("Uploading : ", result);
        if (result.success) {
          setRequiredDocuments(result.documents);
        } else {
          setErrors((prev) => ({
            ...prev,
            fetch: result.error || "Failed to load required documents",
          }));
        }
      } catch (error) {
        setErrors((prev) => ({
          ...prev,
          fetch: "Failed to load required documents. Please refresh the page.",
        }));
      } finally {
        setIsLoadingDocuments(false);
      }
    };

    fetchRequiredDocuments();
  }, [userId, entityId]);

  const isValidParams = useMemo(() => {
    return (
      entityCategory &&
      (entityCategory == "client" || entityCategory == "vendor") &&
      selectedlobtypenationality &&
      (selectedlobtypenationality === "Domestic" ||
        selectedlobtypenationality === "International" ||
        selectedlobtypenationality === "international")
    );
  }, [entityCategory, selectedlobtypenationality]);

  const pageTitle = useMemo(() => {
    if (!isValidParams) return "";
    const nationality =
      selectedlobtypenationality.charAt(0).toUpperCase() +
      selectedlobtypenationality.slice(1).toLowerCase();
    const category = entityCategory === "client" ? "Client" : "Service Provider";
    return `${nationality} ${category} - Upload Documents`;
  }, [entityCategory, selectedlobtypenationality, isValidParams]);

  const handleRemoveFile = (documentId, fileIndex) => {
    const updatedFiles = (uploadedFiles[documentId] || []).filter(
      (_, index) => index !== fileIndex,
    );

    if (updatedFiles.length === 0) {
      const newUploadedFiles = { ...uploadedFiles };
      delete newUploadedFiles[documentId];
      setUploadedFiles(newUploadedFiles);

      const newUploadingStatus = { ...uploadingStatus };
      delete newUploadingStatus[documentId];
      setUploadingStatus(newUploadingStatus);
    } else {
      setUploadedFiles({ ...uploadedFiles, [documentId]: updatedFiles });
    }
  };

  const handleFileSelect = async (e, document) => {
    const files = Array.from(e.target.files);

    if (!files.length || !document) return;
    const documentKey = (document.entityDocumentId && document.entityDocumentId !== 0) ? document.entityDocumentId : document.documentRequirementId;
    const documentRequirementId = document.documentRequirementId;
    const user_id = document.entityId || entityId;
    // Always pass entityDocumentId - use 0 if null/undefined
    const entityDocumentId = document.entityDocumentId || 0;
    const file = files[0];

    // Validate file size (10MB max)
    const maxSize = 10485760; // 10MB
    if (file.size > maxSize) {
      setErrors((prev) => ({
        ...prev,
        [documentKey]: `File size must not exceed ${maxSize / 1048576}MB`,
      }));
      return;
    }

    // Set uploading status
    setUploadingStatus((prev) => ({ ...prev, [documentKey]: "uploading" }));
    setUploadProgress((prev) => ({ ...prev, [documentKey]: 0 }));

    try {
      const uploadCredentials = {
        file,
        documentRequirementId,
        entityId: user_id,
        entityDocumentId: entityDocumentId,
        onProgress: (progress) => {
          setUploadProgress((prev) => ({ ...prev, [documentKey]: progress }));
        },
      };

      const uploadResult = await uploadDocumentAction(uploadCredentials);

      if (uploadResult.success) {
        setUploadedFiles((prev) => ({ ...prev, [documentKey]: [file] }));
        setUploadingStatus((prev) => ({ ...prev, [documentKey]: "success" }));
        setUploadProgress((prev) => ({ ...prev, [documentKey]: 100 }));

        setRequiredDocuments((prevDocs) =>
          prevDocs.map((doc) => {
            const docKey = doc.id || doc.documentRequirementId || doc.requirementId;
            return docKey === documentKey ? { ...doc, isUploaded: true } : doc;
          }),
        );
      } else {
        setErrors((prev) => ({
          ...prev,
          [documentKey]: uploadResult.error || "Upload failed",
        }));
        setUploadingStatus((prev) => ({ ...prev, [documentKey]: "error" }));
        setUploadProgress((prev) => ({ ...prev, [documentKey]: 0 }));
        toast.error(uploadResult.message);

      }
    } catch (error) {
      setErrors((prev) => ({
        ...prev,
        [documentKey]: "Failed to upload. Please try again.",
      }));
      setUploadingStatus((prev) => ({ ...prev, [documentKey]: "error" }));
      setUploadProgress((prev) => ({ ...prev, [documentKey]: 0 }));
    }
  };

  return {
    uploadedFiles,
    errors,
    pageTitle,
    entityName,
    entityId,
    isValidParams,
    uploadingStatus,
    uploadProgress,
    requiredDocumentsField,
    isLoadingDocuments,
    handleRemoveFile,
    handleFileSelect,
  };
};
